import { MouseEvent, useMemo, useState } from 'react';

import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TagIcon from '@mui/icons-material/Tag';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import { alpha } from '@mui/material/styles';
import { frostedPanelBg } from 'src/common/styles/frostedPanelStyles';
import { useTheme } from '@mui/material/styles';
import find from 'lodash-es/find';
import { bindMenu, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';
import { useTranslation } from 'react-i18next';
import { blue } from '@mui/material/colors';

import RegistrationAvatar from 'src/common/components/RegistrationAvatar';
import TicketCard from 'src/common/components/styled/TicketCard';
import { GENDERS_BY_NAME } from 'src/common/constants/genders';
import { LanguageKeys } from 'src/common/constants/languages';
import { getParticipantRegistrationState, registrationSignupAt } from 'src/helpers/participantList';
import {
  formatRegistrationSignupInfo,
  getParticipantDisplayAge,
  getRegistrationDisplayPrice,
} from 'src/helpers/registrationPrice';
import { canRegisterParticipant } from 'src/helpers/userCheckinWizard';
import { ICompetition } from 'src/interfaces/Competition';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import { IRegistration } from 'src/interfaces/Registration';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import useCheckinsStore from 'src/stores/checkinsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import ParticipantQrDialog from './ParticipantQrDialog';

interface Props {
  checkin?(id?: string, e?: MouseEvent<unknown>): void;
  register?(id?: string, e?: MouseEvent<unknown>): void;
  editRegistration?(id?: string, e?: MouseEvent<unknown>): void;
  deleteRegistration?(id?: string, e?: MouseEvent<unknown>): void;
  participant: IEnrichedParticipant;
  deleteParticipant(id: string, e?: MouseEvent<unknown>): void;
  editParticipant(id?: string, e?: MouseEvent<unknown>): void;
  transferParticipant?(id: string, e?: MouseEvent<unknown>): void;
  onDeleteCheckin?: (checkinId: string, e?: MouseEvent<unknown>) => void;
  onUpdatePrice?: (registrationId: string, price: number) => void;
  registrationId?: string;
  isTourRegisterAnchor?: boolean;
  isTourQrAnchor?: boolean;
  isAdminTourMenuAnchor?: boolean;
  isAdminTourRegisterAnchor?: boolean;
  isAdminTourCheckinAnchor?: boolean;
}

const ParticipantCard = ({
  checkin,
  register,
  editRegistration,
  deleteRegistration,
  participant,
  deleteParticipant,
  editParticipant,
  transferParticipant,
  onDeleteCheckin,
  onUpdatePrice,
  registrationId,
  isTourRegisterAnchor = false,
  isTourQrAnchor = false,
  isAdminTourMenuAnchor = false,
  isAdminTourRegisterAnchor = false,
  isAdminTourCheckinAnchor = false,
}: Props) => {
  const {
    age: ageFromParticipant,
    city,
    checkedIn,
    dateOfBirth,
    discounted,
    email,
    firstName,
    gender,
    id,
    lastName,
    sportsGroupName,
    phoneNumber,
    registration,
    checkin: checkinData,
  } = participant;
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const theme = useTheme();
  const { palette } = theme;
  const { activeEditionId, competitions, dateFormat, distances, editionsById } =
    useSystemOptionsStore(systemOptionsState => systemOptionsState);
  const eventName =
    activeEditionId && editionsById?.[activeEditionId]
      ? `${t('base.defaultTitle')} ${editionsById[activeEditionId].year}`
      : t('base.defaultTitle');
  const registerForEditionLabel = t('registrations.registerForEdition', { eventName });
  const { checkinsById } = useCheckinsStore(checkinsState => checkinsState);

  const displayAge = useMemo(() => {
    const edition =
      activeEditionId && editionsById?.[activeEditionId]
        ? editionsById[activeEditionId]
        : null;
    return getParticipantDisplayAge(
      { age: ageFromParticipant, dateOfBirth },
      edition?.year ?? null,
      edition?.date,
    );
  }, [ageFromParticipant, dateOfBirth, activeEditionId, editionsById]);

  const toolsMenu = usePopupState({ variant: 'popover', popupId: 'toolsMenu' });

  const [infoPopoverAnchorEl, setInfoPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);

  const handleInfoButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    setInfoPopoverAnchorEl(event.currentTarget);
  };

  const handleInfoPopoverClose = () => {
    setInfoPopoverAnchorEl(null);
  };

  const infoOpen = Boolean(infoPopoverAnchorEl);
  const moreInfoId = infoOpen ? 'more-info' : undefined;

  const isRegistered = !!(registrationId || registration);
  const registrationData: IRegistration | null = registration || null;
  const registrationState = getParticipantRegistrationState(participant, checkinsById);
  const canEditRegistration = !!editRegistration && registrationState === 'registered';
  const canDeleteRegistration = !!deleteRegistration && registrationState === 'registered';
  const canCheckin = !!checkin && registrationState === 'registered';
  const canRegister = canRegisterParticipant(participant);
  const showMissingDateOfBirth =
    registrationState === 'unregistered' && !!register && !canRegister;
  const competitionId = registrationData?.competitionId || '';
  const distanceId = registrationData?.distanceId || '';
  const displayPrice = useMemo(() => {
    if (!isRegistered) {
      return null;
    }
    const lookupEditionId = registrationData?.editionId || activeEditionId || undefined;
    const edition = lookupEditionId ? editionsById?.[lookupEditionId] : undefined;
    return getRegistrationDisplayPrice(
      registrationData?.price,
      edition,
      { dateOfBirth, age: ageFromParticipant, discounted },
    );
  }, [
    isRegistered,
    registrationData?.price,
    registrationData?.editionId,
    activeEditionId,
    editionsById,
    dateOfBirth,
    ageFromParticipant,
    discounted,
  ]);

  const competition = competitionId ? (find(competitions, c => c.id === competitionId) as ICompetition) : null;
  const competitionName = competition?.i18n[currentLang] || '';

  const distance = distanceId ? (find(distances, d => d.id === distanceId) as TechnicalType) : null;
  const distanceName = distance?.i18n[currentLang] || '';

  const effectiveRegistrationId = registrationId || registration?.id;
  const participantDisplayName =
    displayAge != null ? `${lastName} ${firstName} (${displayAge})` : `${lastName} ${firstName}`;

  const handleCheckin = (e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    if (checkin) {
      checkin(id, e);
    }
  };

  const handleRegister = (e?: MouseEvent<unknown>) => {
    if (register) {
      register(id, e);
    }
  };

  const handleEditRegistration = (e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    if (editRegistration) {
      editRegistration(id, e);
    }
  };

  const handleDeleteRegistration = (e?: MouseEvent<unknown>) => {
    e?.stopPropagation();
    if (deleteRegistration) {
      deleteRegistration(id, e);
    }
  };

  const checkinId = checkinData?.id || checkedIn || undefined;
  const rawSerial = checkinData?.ser ?? (checkedIn && checkinsById?.[checkedIn]?.ser);
  const serialNumber =
    typeof rawSerial === 'number'
      ? rawSerial
      : typeof rawSerial === 'string' && rawSerial !== ''
        ? Number(rawSerial)
        : undefined;
  const hasValidSerial = serialNumber != null && !Number.isNaN(serialNumber);

  const linkActionSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontWeight: 600,
    fontSize: '0.875rem',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    p: 0,
  } as const;

  const statusLabel =
    registrationState === 'unregistered'
      ? t('participants.noRegistration')
      : registrationState === 'checked_in'
        ? t('participants.checkedIn')
        : t('registrations.registered');

  const currentEditionId =
    registrationData?.editionId || activeEditionId || undefined;
  const currentEdition = currentEditionId ? editionsById?.[currentEditionId] : undefined;
  const signupInfo = formatRegistrationSignupInfo(
    t,
    currentEdition,
    registrationSignupAt(registrationData, participant.createdAt),
    dateFormat[currentLang],
  );

  const statusAccentColor =
    registrationState === 'checked_in'
      ? palette.primary.main
      : registrationState === 'registered'
        ? blue[800]
        : palette.secondary.main;

  const cardBackgroundColor =
    registrationState === 'unregistered'
      ? alpha(palette.grey[300], 0.8)
      : frostedPanelBg(theme, 0.8);

  const unregisteredAvatar = register ? (
    <Tooltip title={registerForEditionLabel}>
      <IconButton
        color="primary"
        onClick={handleRegister}
        aria-label={t('registrations.register')}
        {...(isTourRegisterAnchor ? { 'data-tour': 'my-participants-register' } : {})}
        sx={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${palette.grey[400]} 0%, ${palette.grey[200]} 100%)`,
        }}
      >
        <ConfirmationNumberIcon />
      </IconButton>
    </Tooltip>
  ) : (
    <Avatar
      sx={{
        background: `linear-gradient(135deg, ${palette.grey[200]} 0%, ${palette.grey[400]} 100%)`,
      }}
      aria-label="no-competition"
    >
      ?
    </Avatar>
  );

  const participantName =
    displayAge != null ? `${lastName} ${firstName} (${displayAge})` : `${lastName} ${firstName}`;

  const avatarContent =
    competition && distance ? (
      <RegistrationAvatar
        competition={competition}
        distanceLabel={distanceName}
        currentLang={currentLang}
      />
    ) : (
      unregisteredAvatar
    );

  return (
    <TicketCard
      statusStripColor={statusAccentColor}
      sx={{
        backgroundColor: cardBackgroundColor,
      }}
      title={participantName}
      eyebrow={isRegistered && competitionName ? competitionName : undefined}
      description={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <span>{phoneNumber}</span>
          <IconButton
            aria-describedby={moreInfoId}
            onClick={handleInfoButtonClick}
            size="small"
            sx={{
              color: 'secondary.main',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Box>
      }
      avatar={avatarContent}
      actions={
        isRegistered && effectiveRegistrationId ? (
          <Tooltip title={t('participants.showQr')}>
            <IconButton
              size="small"
              color="primary"
              onClick={e => {
                e.stopPropagation();
                setQrDialogOpen(true);
              }}
              aria-label={t('participants.showQr')}
              {...(isTourQrAnchor ? { 'data-tour': 'my-participants-qr' } : {})}
            >
              <QrCode2Icon />
            </IconButton>
          </Tooltip>
        ) : null
      }
      badges={[
        {
          label: statusLabel,
          highlighted: true,
          accent:
            registrationState === 'checked_in'
              ? 'clay'
              : registrationState === 'registered'
                ? 'blue'
                : 'neutral',
          ...(signupInfo ? { tooltip: signupInfo } : {}),
        },
        ...(isRegistered && distanceName ? [{ label: distanceName }] : []),
      ]}
      footer={
        <>
          <IconButton
            {...bindTrigger(toolsMenu)}
            aria-label={t('participants.optionsLabel')}
            {...(isAdminTourMenuAnchor ? { 'data-tour': 'admin-participants-menu' } : {})}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu {...bindMenu(toolsMenu)}>
            <MenuItem
              onClick={e => {
                editParticipant(id, e);
                toolsMenu.close();
              }}
              {...(isAdminTourMenuAnchor ? { 'data-tour': 'admin-participants-edit-item' } : {})}
            >
              <ListItemIcon>
                <EditIcon color="primary" fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('participants.edit')}</ListItemText>
            </MenuItem>
            {checkin && registrationState === 'checked_in' && onDeleteCheckin && checkinId && (
              <MenuItem
                onClick={e => {
                  onDeleteCheckin(checkinId, e);
                  toolsMenu.close();
                }}
              >
                <ListItemIcon>
                  <DeleteForeverIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('checkins.deleteCheckin')}</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={e => {
                deleteParticipant(id, e);
                toolsMenu.close();
              }}
              {...(isAdminTourMenuAnchor ? { 'data-tour': 'admin-participants-delete-item' } : {})}
            >
              <ListItemIcon>
                <DeleteForeverIcon color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('participants.delete')}</ListItemText>
            </MenuItem>
            {transferParticipant && (
              <MenuItem
                onClick={e => {
                  transferParticipant(id!, e);
                  toolsMenu.close();
                }}
                {...(isAdminTourMenuAnchor ? { 'data-tour': 'admin-participants-transfer-item' } : {})}
              >
                <ListItemIcon>
                  <SwapHorizIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('participants.transfer')}</ListItemText>
              </MenuItem>
            )}
          </Menu>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
              ml: 'auto',
            }}
          >
            {displayPrice != null && (
              <>
                <LocalOfferIcon sx={{ fontSize: 20 }} color="action" aria-hidden />
                {editingPrice && registrationId && onUpdatePrice ? (
                  <TextField
                    size="small"
                    type="number"
                    defaultValue={displayPrice}
                    autoFocus
                    sx={{ width: 100 }}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">{t('base.currency')}</InputAdornment>
                        ),
                        inputProps: { min: 0, step: 1 },
                      },
                    }}
                    onBlur={e => {
                      const val = parseFloat(e.target.value);
                      if (!Number.isNaN(val)) onUpdatePrice(registrationId, val);
                      setEditingPrice(false);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') setEditingPrice(false);
                    }}
                  />
                ) : (
                  <>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                      {t('base.currencyValue', { count: displayPrice })}
                    </Typography>
                    {onUpdatePrice && registrationId && (
                      <IconButton size="small" onClick={() => setEditingPrice(true)} sx={{ ml: 0.25, p: 0.25 }}>
                        <EditIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </>
                )}
              </>
            )}
            {registrationState === 'unregistered' && register && canRegister ? (
              <Tooltip title={registerForEditionLabel}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  color="primary"
                  underline="hover"
                  onClick={handleRegister}
                  sx={linkActionSx}
                  {...(isAdminTourRegisterAnchor ? { 'data-tour': 'admin-participants-register' } : {})}
                >
                  <ConfirmationNumberIcon fontSize="small" aria-hidden />
                  {t('registrations.register')}
                </Link>
              </Tooltip>
            ) : null}
          </Box>
        </>
      }
    >
        <Popover
          id={moreInfoId}
          open={infoOpen}
          anchorEl={infoPopoverAnchorEl}
          onClose={handleInfoPopoverClose}
          transformOrigin={{ horizontal: 'center', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          slotProps={{
            paper: {
              style: {
                width: 350,
                padding: 16,
              },
            },
          }}
        >
          <Grid container spacing={1} columnSpacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color={grey[300]}>
                {t('participants.email')}:
              </Typography>
              <Typography variant="body2">{email}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color={grey[300]}>
                {t('participants.city')}:
              </Typography>
              <Typography variant="body2">{city}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="body2" color={grey[300]}>
                {t('participants.gender')}:
              </Typography>
              <Typography variant="body2">
                {t(`base.gender.${GENDERS_BY_NAME[gender]?.name ?? gender}`)}
              </Typography>
            </Grid>
            {checkedIn && checkinsById && checkinsById[checkedIn] ? (
              <Grid key="ser" size={{ xs: 6 }}>
                <Typography variant="body2" color={grey[300]}>
                  {t('checkins.ser')}:
                </Typography>
                <Typography variant="body2">{checkinsById[checkedIn].ser}</Typography>
              </Grid>
            ) : null}
            <Grid size={{ xs: 12 }}>
              {!!sportsGroupName && (
                <>
                  <Typography variant="body2" color={grey[300]}>
                    {t('participants.sportsGroupName')}:
                  </Typography>
                  <Typography variant="body2">{sportsGroupName}</Typography>
                </>
              )}
            </Grid>
          </Grid>
        </Popover>

        <Grid container spacing={1} sx={{ mt: 1, alignItems: 'center' }}>
          <Grid
            size={12}
            sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minWidth: 0, gap: 1 }}
          >
            {(canEditRegistration || canDeleteRegistration || canCheckin) && (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                {canCheckin && (
                  <Tooltip title={t('participants.checkinParticipant')}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      color="primary"
                      underline="hover"
                      onClick={handleCheckin}
                      sx={linkActionSx}
                      aria-label={t('participants.checkinParticipant')}
                      {...(isAdminTourCheckinAnchor ? { 'data-tour': 'admin-participants-checkin' } : {})}
                    >
                      <LocalActivityIcon fontSize="small" aria-hidden />
                    </Link>
                  </Tooltip>
                )}
                {canEditRegistration && (
                  <Tooltip title={t('registrations.editRegistration')}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      color="primary"
                      underline="hover"
                      onClick={handleEditRegistration}
                      sx={linkActionSx}
                      aria-label={t('registrations.editRegistration')}
                    >
                      <EditDocumentIcon fontSize="small" aria-hidden />
                    </Link>
                  </Tooltip>
                )}
                {canDeleteRegistration && (
                  <Tooltip title={t('registrations.deleteRegistration')}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      color="error"
                      underline="hover"
                      onClick={handleDeleteRegistration}
                      sx={linkActionSx}
                      aria-label={t('registrations.deleteRegistration')}
                    >
                      <DeleteForeverIcon fontSize="small" aria-hidden />
                    </Link>
                  </Tooltip>
                )}
              </Box>
            )}
            {registrationState === 'checked_in' && (
              <Link
                component="span"
                variant="body2"
                color="black"
                underline="none"
                sx={{ ...linkActionSx, cursor: 'default' }}
              >
                <TagIcon fontSize="small" aria-hidden />
                {hasValidSerial ? serialNumber : '-'}
              </Link>
            )}
          </Grid>
        </Grid>
        {showMissingDateOfBirth ? (
          <Typography variant="caption" color="error" component="p" sx={{ mt: 1.5, mb: 0 }}>
            {t('userCheckinWizard.missingDateOfBirth')}
          </Typography>
        ) : null}
      {effectiveRegistrationId && (
        <ParticipantQrDialog
          open={qrDialogOpen}
          onClose={() => setQrDialogOpen(false)}
          registrationId={effectiveRegistrationId}
          participantName={participantDisplayName}
        />
      )}
    </TicketCard>
  );
};

export default ParticipantCard;
